// ============================================================================
// CHANGO V5.0 — D3/D9/D10 App Dashboard (post-login concourse)
// ============================================================================
// Replaces the D1.5 stub Dashboard for users navigating into /app/dashboard.
// Composition:
//   - 4 KPI tiles (报价 / 客户 / 待审 / 本周成交)  — D9 backed by /api/dashboard/kpis
//   - "最近活动" feed                              — D10 backed by /api/activity/feed
//   - "需要您处理" pending-quotes shortcut         — D10 backed by /api/quotes?status=pending
//
// §20 #17: the "本月利润" tile is wrapped in <HideFromSales>. Sales users see
// "本月已成交单数" instead — same row, different content. The activity feed
// is already sales-scrubbed server-side (activity.js stripSalesPayload).
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth.js';
import { api, HttpError } from '../../api/client.js';
import { HideFromSales, SalesOnly } from '../../components/RoleGate.jsx';
import { hasWorkspaceLayer } from '../../utils/layerPermissions.js';

function KpiTile({ label, value, hint, href }) {
  const inner = (
    <div className="card p-5 hover:shadow-md transition-shadow">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold text-slate-800">
        {value ?? '—'}
      </div>
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    </div>
  );
  return href ? <Link to={href}>{inner}</Link> : inner;
}

function useDashboardKpis() {
  const [data, setData] = useState({
    quotes_total: null,
    customers_total: null,
    quotes_pending: null,
    closed_this_week: null,
    profit_this_month: null
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // The aggregated /api/dashboard/kpis endpoint will land in D6;
        // until then we degrade gracefully — placeholders are shown.
        const res = await api.get('/api/dashboard/kpis');
        if (!cancelled) setData(res || {});
      } catch (err) {
        if (cancelled) return;
        if (err instanceof HttpError && err.status === 404) {
          // expected — endpoint not yet implemented
        } else {
          setError(err.message || '加载失败');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { data, error };
}

export default function AppDashboard() {
  const { user, workspace } = useAuth();
  const { data, error } = useDashboardKpis();
  const role = user?.role;
  const bossOrFinance = role === 'boss' || role === 'finance';
  const hasA = hasWorkspaceLayer(workspace, 'A');
  const hasB = hasWorkspaceLayer(workspace, 'B');
  const hasC = hasWorkspaceLayer(workspace, 'C');
  const hasE = hasWorkspaceLayer(workspace, 'E');

  return (
    <div className="px-6 py-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-800">
          欢迎回来，{user?.email?.split('@')[0] || '同事'}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {workspace?.name} · {workspace?.registration_status || '审核中'}
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile
          label="报价总数"
          value={data.quotes_total}
          hint="所有状态合计"
          href="/app/quotes"
        />
        <KpiTile
          label="客户总数"
          value={data.customers_total}
          href="/app/customers"
        />
        <KpiTile
          label="待审报价"
          value={data.quotes_pending}
          hint="finance 审批通道"
          href="/app/quotes?status=pending"
        />
        <HideFromSales fallback={null}>
          <KpiTile
            label="本月利润 (RMB)"
            value={data.profit_this_month != null
              ? Number(data.profit_this_month).toLocaleString('zh-CN', {
                  minimumFractionDigits: 2, maximumFractionDigits: 2
                })
              : null}
            hint="boss / finance 可见"
          />
        </HideFromSales>
        <SalesOnly>
          <KpiTile
            label="本周成交单数"
            value={data.closed_this_week}
            hint="您参与的"
          />
        </SalesOnly>
      </div>

      <WorkflowPanel
        bossOrFinance={bossOrFinance}
        hasA={hasA}
        hasB={hasB}
        hasC={hasC}
        hasE={hasE}
      />

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <PendingPanel />
        <ActivityPanel />
      </div>
    </div>
  );
}

function WorkflowPanel({ bossOrFinance, hasA, hasB, hasC, hasE }) {
  const steps = [
    {
      stage: '1',
      title: '客户',
      text: '先确定客户、联系人和报价历史。',
      href: '/app/customers',
      enabled: true
    },
    {
      stage: '2',
      title: '工厂',
      text: hasA ? '维护产品、物料、BOM 和工序成本。' : '维护产品目录，作为报价行项目来源。',
      href: hasA && bossOrFinance ? '/app/factory/bom-items' : '/app/products',
      enabled: true
    },
    {
      stage: '3',
      title: '外贸',
      text: hasB ? '生成外贸报价，处理汇率、退税、审批和交付。' : '当前套餐未开通 B 层外贸能力。',
      href: '/app/quotes',
      enabled: true
    },
    {
      stage: '4',
      title: '电商',
      text: '接外贸报价，计算平台佣金、广告、FBA、退货后的渠道净利。',
      href: '/app/crossborder',
      enabled: bossOrFinance && hasC
    },
    {
      stage: '5',
      title: '跨境主体',
      text: '接渠道/外贸利润，计算主体分配、海外税和集团净利。',
      href: '/app/entities',
      enabled: bossOrFinance && hasE
    }
  ];

  return (
    <section className="mt-8 rounded-md border border-slate-200 bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">报价决策链路</h2>
        {bossOrFinance && (
          <Link to="/app/quotes/new" className="text-sm font-medium text-chango-700 hover:underline">
            新建报价 →
          </Link>
        )}
      </div>
      <div className="grid gap-3 md:grid-cols-5">
        {steps.map((step) => {
          const content = (
            <div className={[
              'h-full rounded-md border p-4 transition',
              step.enabled
                ? 'border-slate-200 bg-white hover:border-chango-200 hover:bg-chango-50/40'
                : 'border-slate-100 bg-slate-50 opacity-70'
            ].join(' ')}>
              <div className="mb-3 inline-flex h-7 w-7 items-center justify-center rounded bg-slate-100 text-sm font-semibold text-slate-800">
                {step.stage}
              </div>
              <div className="font-semibold text-slate-900">{step.title}</div>
              <p className="mt-2 text-xs leading-5 text-slate-700">{step.text}</p>
            </div>
          );
          return step.enabled
            ? <Link key={step.stage} to={step.href}>{content}</Link>
            : <div key={step.stage}>{content}</div>;
        })}
      </div>
    </section>
  );
}

// ---- "需要您处理" — pending approval queue --------------------------------

function usePendingQuotes() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/quotes?status=pending&limit=5');
        if (cancelled) return;
        setRows(Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []));
      } catch (e) {
        if (cancelled) return;
        setErr(e instanceof HttpError ? `${e.code}: ${e.message}` : (e.message || '加载失败'));
        setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { rows, err };
}

function PendingPanel() {
  const { rows, err } = usePendingQuotes();
  return (
    <section className="card p-5">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-800">需要您处理</h2>
        <Link to="/app/quotes?status=pending" className="text-xs text-chango-700 hover:underline">
          查看全部 →
        </Link>
      </header>
      {err && <div className="alert-error mb-2">{err}</div>}
      {rows == null && <div className="text-sm text-slate-400">加载中…</div>}
      {Array.isArray(rows) && rows.length === 0 && (
        <div className="text-sm text-slate-500">
          无待审报价。继续：
          {' '}<Link to="/app/quotes/new" className="text-chango-700 hover:underline">新建报价</Link>{' '}·{' '}
          <Link to="/app/customers" className="text-chango-700 hover:underline">维护客户</Link>。
        </div>
      )}
      {Array.isArray(rows) && rows.length > 0 && (
        <ul className="divide-y divide-slate-100 text-sm">
          {rows.map((q) => (
            <li key={q.id} className="flex items-center justify-between py-2">
              <div className="min-w-0">
                <Link to={`/app/quotes/${q.id}`} className="font-medium text-slate-800 hover:underline">
                  {q.code || `Q-${q.id}`}
                </Link>
                <div className="truncate text-xs text-slate-500">
                  {q.customer_name || '—'}
                </div>
              </div>
              <span className="ml-3 shrink-0 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                待审
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---- "最近活动" — activity feed --------------------------------------------

const EVENT_LABEL = {
  'quote.created':       '创建报价',
  'quote.submitted':     '提交审批',
  'quote.approved':      '审批通过',
  'quote.rejected':      '已拒绝',
  'quote.issued':        '出具报价',
  'quote.closed_won':    '成交',
  'quote.closed_lost':   '流失',
  'customer.created':    '新增客户',
  'customer.updated':    '更新客户',
  'comment.created':     '新评论',
  'member.invited':      '邀请成员',
  'member.joined':       '成员加入'
};

function useActivityFeed() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/activity/feed?limit=8');
        if (cancelled) return;
        setRows(Array.isArray(res?.events) ? res.events : []);
      } catch (e) {
        if (cancelled) return;
        if (e instanceof HttpError && e.status === 404) {
          setRows([]); // endpoint absent in some environments
          return;
        }
        setErr(e instanceof HttpError ? `${e.code}: ${e.message}` : (e.message || '加载失败'));
        setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { rows, err };
}

function ActivityPanel() {
  const { rows, err } = useActivityFeed();
  return (
    <section className="card p-5">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-800">最近活动</h2>
      </header>
      {err && <div className="alert-error mb-2">{err}</div>}
      {rows == null && <div className="text-sm text-slate-400">加载中…</div>}
      {Array.isArray(rows) && rows.length === 0 && (
        <div className="text-sm text-slate-500">近期暂无活动。</div>
      )}
      {Array.isArray(rows) && rows.length > 0 && (
        <ul className="divide-y divide-slate-100 text-sm">
          {rows.map((ev) => (
            <ActivityRow key={ev.id} ev={ev} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ActivityRow({ ev }) {
  const label = EVENT_LABEL[ev.event_type] || ev.event_type;
  const entityHref = ev.entity_type === 'quote'
    ? `/app/quotes/${ev.entity_id}`
    : ev.entity_type === 'customer'
      ? `/app/customers`
      : null;
  const when = ev.created_at ? new Date(ev.created_at).toLocaleString('zh-CN') : '—';
  return (
    <li className="flex items-center justify-between py-2">
      <div className="min-w-0">
        {entityHref
          ? <Link to={entityHref} className="font-medium text-slate-800 hover:underline">{label}</Link>
          : <span className="font-medium text-slate-800">{label}</span>}
        <div className="truncate text-xs text-slate-500">
          {ev.entity_type} #{ev.entity_id}
          {ev.payload?.code && ` · ${ev.payload.code}`}
        </div>
      </div>
      <span className="ml-3 shrink-0 text-xs text-slate-400">{when}</span>
    </li>
  );
}
